import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import CashoutHeader from './CashoutHeader';
import CashoutBottom from './CashoutBottom';
import CashoutOnboardingBanner from './CashoutOnboardingBanner';
import CashoutRedemptionBanner from './CashoutRedemptionBanner';
import CashoutWarningBanner from './CashoutWarningBanner';
import CashoutTaxStatus from './CashoutTaxStatus';
import CashoutOnboardingForm from './forms/CashoutOnboardingForm';
import ClientSideConfigSelector from '../../../recoil/cashout/ClientSideConfigSelector';
import useHttpRequest from '../../api/HttpRequest';
import useTaxAccount from './hooks/useTaxAccount';

import DiamondBalance from '../common/DiamondBalance';

import CashoutContent from './CashoutContent';
import { RegistrationStatus } from '../../../../client/claim-reward/constants';

export default function Cashout() {
  const httpRequest = useHttpRequest();
  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
        displayRedemptionBanner = false,
        providers: {
          [paymentProvider]: {
            displayOnboardingBanner = false,
            cashoutWarnings = ['Last payment failed'],
          } = {},
        } = {},
      } = {},
    } = {},
  } = config || {};

  const { taxAccount, refreshTaxAccount } = useTaxAccount();

  const [payAccount, setPayAccount] = useState(null);
  const [kycAccount, setKycAccount] = useState(null);

  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);
  const [showRedemptionBanner, setShowRedemptionBanner] = useState(false);
  const [showUpdatePaymentMethod, setShowUpdatePaymentMethod] = useState(false);

  const refreshPayStatus = useCallback(async () => {
    if (!paymentProvider) return;

    setPayAccount(null);
    setKycAccount(null);

    try {
      const result = await httpRequest.send(
        `/web-payments/cash-reward/account-status/${paymentProvider}?forceRefresh=true`,
      );
      const {
        providers: {
          pay,
          kyc,
        } = {},
      } = result;

      setPayAccount(pay);
      setKycAccount(kyc);
    } catch (err) {
      console.log('Unable to fetch payment provider account status', err);
    }
    // eslint-disable-next-line
  }, [paymentProvider]);

  const refreshAccountStatus = useCallback(async () => {
    await refreshPayStatus();
    await refreshTaxAccount();
  }, [refreshPayStatus, refreshTaxAccount]);

  useEffect(() => {
    refreshAccountStatus();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!payAccount) {
      setShowOnboardingBanner(false);
      return;
    }

    const {
      status = RegistrationStatus.Unregistered,
    } = payAccount;

    const shouldShow = displayOnboardingBanner && status === RegistrationStatus.Unregistered;

    setShowOnboardingBanner(shouldShow);
  }, [displayOnboardingBanner, payAccount]);

  useEffect(() => {
    if (!payAccount) {
      setShowWarningBanner(false);
      return;
    }

    const {
      status = RegistrationStatus.Unregistered,
      warnings = [],
    } = payAccount;

    const shouldShow = status !== RegistrationStatus.RegisteredVerified
      && cashoutWarnings.some((warning) => warnings?.includes(warning));

    setShowWarningBanner(shouldShow);
  }, [cashoutWarnings, payAccount]);

  useEffect(() => {
    setShowRedemptionBanner(displayRedemptionBanner);
  }, [displayRedemptionBanner]);

  return (
    <div>
      <DiamondBalance />

      {showOnboardingBanner && (
        <CashoutOnboardingBanner onClose={() => setShowOnboardingBanner(false)} />
      )}

      {showRedemptionBanner && <CashoutRedemptionBanner />}

      {showWarningBanner && (
        <CashoutWarningBanner
          onPaymentMethodUpdate={() => setShowUpdatePaymentMethod(true)}
        />
      )}

      <CashoutTaxStatus />

      <CashoutHeader />

      <CashoutContent
        payAccount={payAccount}
        kycAccount={kycAccount}
        taxAccount={taxAccount}
        onRefresh={refreshAccountStatus}
      />

      {showUpdatePaymentMethod && (
        <CashoutOnboardingForm
          payAccount={payAccount}
          onClose={async () => {
            setShowUpdatePaymentMethod(false);
            await refreshAccountStatus();
          }}
        />
      )}

      <CashoutBottom />
    </div>
  );
}
